import React from "react";
import { ChevronDown } from "lucide-react";
import { GroupDTO } from "@/generated-client";
import { readGroupActivity } from "@/main/common/GroupActivityTypes";
import { Localizer } from "@/main/react/Types";
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { getOptimizedSeavienImageUrl, SMALL_THUMBNAIL_IMAGE_SIZE } from "@/main/util/ImageUrlHelper";

type DiscussionGroupSummaryLocalizationKey =
    | "discussion.section.groups"
    | "discussion.groups.summary.in"
    | "discussion.groups.summary.and_others"
    | "discussion.groups.summary.group_only";

function getGroupMembershipRank(group: GroupDTO): number {
    return group.isUserPartOfGroup || group.isUserLeaderOfGroup ? 1 : 0;
}

function localize(localizer: Localizer, key: DiscussionGroupSummaryLocalizationKey, ...args: string[]): string {
    return localizer(key, ...args);
}

/**
 * Honest community size (2026-09-10; this was the member count). Only the numbers the server chose to
 * send are compared, so a group below the floor, or a private group the viewer is outside, sorts as
 * quiet here exactly as it renders as quiet. Contributions outrank visitors, as in the server's
 * `activity_rank`; the server's order for a fetched page is already exact, and this only has to keep a
 * prepended group from jumping ahead of busier ones.
 */
function activityRank(group: GroupDTO): number {
    const activity = readGroupActivity(group);
    if (activity.kind !== "activity") {
        return 0;
    }
    return (activity.contributionsThisWeek ?? 0) * 1_000_000 + (activity.visitorsThisWeek ?? 0);
}

export function compareDiscussionGroups(left: GroupDTO, right: GroupDTO): number {
    const rankDiff = getGroupMembershipRank(left) - getGroupMembershipRank(right);
    if (rankDiff !== 0) {
        return rankDiff;
    }

    const activityDiff = activityRank(right) - activityRank(left);
    if (activityDiff !== 0) {
        return activityDiff;
    }

    const rightGroupId = Number(right.groupId ?? Number.MIN_SAFE_INTEGER);
    const leftGroupId = Number(left.groupId ?? Number.MIN_SAFE_INTEGER);
    if (rightGroupId !== leftGroupId) {
        return rightGroupId - leftGroupId;
    }

    return (right.publicId ?? "").localeCompare(left.publicId ?? "");
}

export function mergeDiscussionGroups(
    fetchedGroups: GroupDTO[],
    prependGroups: GroupDTO[] = [],
    removedGroupIds: Set<string> = new Set(),
): GroupDTO[] {
    const merged = new Map<string, GroupDTO>();

    const addGroup = (group?: GroupDTO | null) => {
        const publicId = group?.publicId?.trim();
        if (!group || !publicId || removedGroupIds.has(publicId) || merged.has(publicId)) {
            return;
        }
        merged.set(publicId, group);
    };

    // FI-161: a 200 with a non-array body reached here and `forEach` threw inside DiscussionPage's
    // `useMemo`, deleting the island. A decorative list degrades to empty instead (rule 32).
    if (Array.isArray(prependGroups)) {
        prependGroups.forEach(addGroup);
    }
    if (Array.isArray(fetchedGroups)) {
        fetchedGroups.forEach(addGroup);
    }

    return Array.from(merged.values()).sort(compareDiscussionGroups);
}

export function DiscussionGroupSummarySkeleton(props: {
    className?: string;
    showPrivateAccessBadge?: boolean;
}) {
    const { className, showPrivateAccessBadge = false } = props;

    return (
        <div
            className={cn(
                "flex min-w-0 flex-wrap items-center gap-3 text-sm text-muted-foreground",
                className,
            )}
            data-playwright-id="discussion-group-summary-skeleton"
        >
            <div className="flex shrink-0 -space-x-2">
                <Skeleton className="size-8 rounded-full border-2 border-card" />
                <Skeleton className="size-8 rounded-full border-2 border-card" />
                <Skeleton className="size-8 rounded-full border-2 border-card" />
            </div>

            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <Skeleton className="h-4 w-48 max-w-full" />
                {showPrivateAccessBadge ? <Skeleton className="h-6 w-20 rounded-full" /> : null}
            </div>
        </div>
    );
}

function getAvatarFallback(name?: string | null): string {
    const trimmedName = name?.trim();
    if (!trimmedName) {
        return "G";
    }

    return trimmedName.charAt(0).toUpperCase();
}

function getGroupHref(group: GroupDTO): string | undefined {
    return group.slug ? `/group/${encodeURIComponent(group.slug)}` : undefined;
}

// One row in the overflow dropdown: the group's image and name,
// linking to the group. Renders as a plain <div> row when the group has no slug to link to.
export function GroupOverflowRow({ group, localizer }: { group: GroupDTO; localizer: Localizer }) {
    const href = getGroupHref(group);
    const avatarImageUrl = getOptimizedSeavienImageUrl(group.thumbnailUrl, SMALL_THUMBNAIL_IMAGE_SIZE) || undefined;
    const name = group.name ?? localize(localizer, "discussion.section.groups");

    // The whole row is the link. It carried a per-row `GroupButtons` for one day (D1, 2026-08-30,
    // reverted 2026-08-31): a disclosure that answers "which groups is this in?" is a list, not an
    // action surface, and repeating one CTA once per row made it neither. Keep it a link.
    const rowClass = "flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-accent";
    const content = (
        <>
            <Avatar size="sm" className="shrink-0 bg-muted">
                <AvatarImage src={avatarImageUrl} alt={group.name ?? undefined} />
                <AvatarFallback>{getAvatarFallback(group.name)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-text-main">{name}</div>
            </div>
        </>
    );

    return href ? (
        <a href={href} className={rowClass} aria-label={name}>
            {content}
        </a>
    ) : (
        <div className={rowClass}>{content}</div>
    );
}

// The "and N more groups" tail rendered as a dropdown: clicking it opens a compact, scrollable popover
// listing every group the discussion is in (image + name), each a link to its group
// page — so a discussion posted to several groups exposes all of them from the header.
function GroupsOverflowPopover({
    groups,
    otherGroupsCount,
    localizer,
}: {
    groups: GroupDTO[];
    otherGroupsCount: number;
    localizer: Localizer;
}) {
    return (
        <Popover>
            <PopoverTrigger
                type="button"
                aria-label={localize(localizer, "discussion.section.groups")}
                data-playwright-id="discussion-groups-overflow-trigger"
                /*
                 * `DISCUSSION-31`. Two things landed here, both at the call site rather than in the
                 * utility.
                 *
                 * `[@media(pointer:coarse)]:min-h-11`, NOT `touch-hit-44`: this trigger sits directly
                 * beneath the primary group link, closer than 44px, so a centred transparent slug
                 * would overlap it and STEAL ITS TAPS — the pitch constraint documented at
                 * `css/03-tap-targets.css:70`. Growing the control itself puts the pitch at
                 * `44 + gap` by construction. And it is POINTER-gated, never `sm:`-gated: a floor on
                 * a viewport breakpoint leaves a touchscreen laptop and a narrow desktop window both
                 * wrong in opposite directions, which `scripts/check-tap-target-mechanisms.mjs`
                 * check C fails the build for.
                 *
                 * The focus ring is the app-wide standard triple. It was a per-component
                 * `ring-2 ring-ring/70` — one more ring than the design language allows there to be.
                 */
                className="inline-flex items-center gap-0.5 rounded-sm font-medium text-text-main underline decoration-dotted underline-offset-2 touch-manipulation hover:decoration-solid [@media(pointer:coarse)]:min-h-11 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
                <span>{localize(localizer, "discussion.groups.summary.and_others", String(otherGroupsCount))}</span>
                <ChevronDown className="size-3.5 shrink-0" aria-hidden="true" />
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-72 p-1"
                data-playwright-id="discussion-groups-overflow-menu"
            >
                <div className="max-h-72 overflow-y-auto">
                    {groups.map((group, index) => (
                        <GroupOverflowRow key={group.publicId ?? `group-${index}`} group={group} localizer={localizer} />
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default function DiscussionGroupSummary({
    groups,
    localizer,
    className,
    maxVisibleAvatars = 3,
    showPrivateAccessBadge = false,
}: {
    groups: GroupDTO[];
    localizer: Localizer;
    className?: string;
    maxVisibleAvatars?: number;
    showPrivateAccessBadge?: boolean;
}) {
    if (!groups.length) {
        return null;
    }

    const primaryGroup = groups[0];
    const otherGroupsCount = Math.max(0, groups.length - 1);
    const visibleGroups = groups.slice(0, maxVisibleAvatars);
    const hiddenGroupsCount = Math.max(0, groups.length - visibleGroups.length);

    return (
        <div
            className={cn(
                "flex min-w-0 flex-wrap items-center gap-3 text-sm text-muted-foreground",
                className,
            )}
            data-playwright-id="discussion-group-summary"
        >
            {/* `DISCUSSION-80` gave this stack a FIXED leading column (`min-w-[4.5rem]`, the widest stack)
                so this row and its series twin shared one label origin. `DISCUSSION-120` (V52 a): since U39
                (`DISCUSSION-96`, `-123`) the page renders ONE combined provenance line whenever a post has
                both groups and series, so the two rows never stand together any more, and the floor only
                padded a lone one-chip stack ~60 CSS px short of its label. Keep the two twins identical.

                `DISCUSSION-119` (V52 a): the stack is DECORATIVE. Each avatar used to be its own ~24 px
                link, overlapped by its neighbour, beside the text link to the same place, so a thumb could
                open the wrong group and the keyboard met duplicate tab stops. Navigation is the text link
                and the overflow popover; the stack is hidden from AT and holds no anchors. */}
            <AvatarGroup className="shrink-0" aria-hidden="true" data-playwright-id="discussion-group-summary-avatars">
                {visibleGroups.map((group, index) => {
                    // `publicId` is the natural key but it is optional on the DTO, and React treats an
                    // `undefined` key as no key at all. The index tail only ever applies to a group the
                    // list could not otherwise name.
                    const key = group.publicId ?? `group-${index}`;
                    const avatarImageUrl = getOptimizedSeavienImageUrl(group.thumbnailUrl, SMALL_THUMBNAIL_IMAGE_SIZE) || undefined;
                    return (
                        <Avatar key={key} size="sm" className="bg-muted">
                            <AvatarImage src={avatarImageUrl} alt="" />
                            <AvatarFallback>{getAvatarFallback(group.name)}</AvatarFallback>
                        </Avatar>
                    );
                })}
                {hiddenGroupsCount > 0 ? (
                    <AvatarGroupCount>+{hiddenGroupsCount}</AvatarGroupCount>
                ) : null}
            </AvatarGroup>

            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <div className="min-w-0">
                    <span>{localize(localizer, "discussion.groups.summary.in")} </span>
                    {/* `DISCUSSION-80`, second half — the group's NAME does not break across lines.
                        Under Spanish the longer lead-in pushed the name past the line end and severed it
                        ("New in" / "spanish y en 2 grupos más"), which left the "and N others"
                        disclosure reading as attached to a fragment and the visible phrase naming no
                        group at all. `inline-block` + `truncate` keeps the name whole and moves the whole
                        box to the next line when it will not fit, ellipsising only when the name alone
                        exceeds the column. `align-bottom` because `overflow: hidden` moves an
                        inline-block's baseline to its bottom edge, which would otherwise lift the name
                        off the lead-in's baseline. */}
                    {primaryGroup.slug ? (
                        <a
                            href={`/group/${encodeURIComponent(primaryGroup.slug)}`}
                            className="inline-block max-w-full truncate align-bottom font-medium text-text-main hover:underline"
                        >
                            {primaryGroup.name ?? localize(localizer, "discussion.section.groups")}
                        </a>
                    ) : (
                        <span className="inline-block max-w-full truncate align-bottom font-medium text-text-main">
                            {primaryGroup.name ?? localize(localizer, "discussion.section.groups")}
                        </span>
                    )}
                    {otherGroupsCount > 0 ? (
                        <>
                            {" "}
                            <GroupsOverflowPopover
                                groups={groups}
                                otherGroupsCount={otherGroupsCount}
                                localizer={localizer}
                            />
                        </>
                    ) : null}
                </div>
                {showPrivateAccessBadge ? (
                    <span className="rounded-full border border-primary/20 bg-primary/8 px-2.5 py-1 text-[0.65rem] font-medium uppercase tracking-[0.08em] text-primary">
                        {localize(localizer, "discussion.groups.summary.group_only")}
                    </span>
                ) : null}
            </div>
        </div>
    );
}
